import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  Link,
  CircularProgress,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { Visibility, VisibilityOff, Lock, Login } from "@mui/icons-material";
import { signIn, confirmSignIn } from "aws-amplify/auth";
import { AtomicLogo } from "../../../assets/AtomicLogo";

interface PasswordEntryProps {
  email: string;
  onSwitchToForgotPassword: () => void;
  onSwitchToLanding: () => void;
  onSignInSuccess: () => void;
}

export const PasswordEntryComponent = ({
  email,
  onSwitchToForgotPassword,
  onSwitchToLanding,
  onSignInSuccess,
}: PasswordEntryProps) => {
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [needsNewPassword, setNeedsNewPassword] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const result = await signIn({
        username: email,
        password: password,
      });
      console.log("Sign in result:", result);

      if ((result as any)?.isSignedIn) {
        console.log("User is signed in successfully");
        onSignInSuccess();
        return;
      }
      const nextStep = (result as any)?.nextStep;
      console.log("Sign in nextStep:", nextStep);

      if (
        nextStep?.signInStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD" ||
        nextStep?.signInStep === "NEW_PASSWORD_REQUIRED" ||
        result?.nextStep?.signInStep ===
          "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED" ||
        nextStep?.signInStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED"
      ) {
        console.log("Detected force change password challenge");
        setNeedsNewPassword(true);
        return;
      }

      // Log what we got instead
      console.warn(
        "Unknown signInStep:",
        nextStep?.signInStep,
        "Full nextStep:",
        nextStep
      );
      // If unknown intermediate step, surface a generic error
      setError("Additional authentication step is required. Please try again.");
    } catch (err: any) {
      console.error("Sign in error:", err);

      // Handle specific error cases
      if (err.name === "NotAuthorizedException") {
        setError("Email or password wrong.");
      } else if (err.name === "UserNotConfirmedException") {
        setError("Please verify your email address before signing in.");
      } else if (err.name === "TooManyRequestsException") {
        setError("Too many failed attempts. Please try again later.");
      } else if (err.name === "InvalidPasswordException") {
        // This can happen when user has force change password status
        // and the temporary password is being validated
        const result = err as any;
        if (
          result?.nextStep?.signInStep ===
            "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD" ||
          result?.nextStep?.signInStep === "NEW_PASSWORD_REQUIRED" ||
          result?.nextStep?.signInStep ===
            "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED"
        ) {
          setNeedsNewPassword(true);
          return;
        }
        setError("Email or password wrong.");
      } else {
        // Check if this is a challenge response for force change password
        const result = err as any;
        if (
          result?.nextStep?.signInStep ===
            "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD" ||
          result?.nextStep?.signInStep === "NEW_PASSWORD_REQUIRED" ||
          result?.nextStep?.signInStep ===
            "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED"
        ) {
          setNeedsNewPassword(true);
          return;
        }
        setError(
          err.message || "An error occurred during sign in. Please try again."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleSubmitNewPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    if (!newPassword || newPassword !== confirmNewPassword) {
      setError("Passwords do not match.");
      setLoading(false);
      return;
    }
    try {
      console.log("Attempting to confirm sign in with new password");
      const result = await confirmSignIn({
        challengeResponse: newPassword,
      });
      console.log("Confirm sign in result:", result);
      console.log("Authentication successful, calling onSignInSuccess");
      onSignInSuccess();
    } catch (err: any) {
      console.error("Confirm new password error:", err);
      console.error("Error details:", {
        name: err.name,
        message: err.message,
        code: err.code,
        stack: err.stack,
      });
      setError(err?.message || "Failed to set new password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (needsNewPassword) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          background: "#fafafa",
          padding: 2,
        }}
      >
        <Card
          sx={{
            maxWidth: 400,
            width: "100%",
            borderRadius: 2,
            border: "1px solid #e0e0e0",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
          }}
        >
          <CardContent sx={{ padding: 4 }}>
            <Box sx={{ textAlign: "center", mb: 3 }}>
              <Box sx={{ mb: 2 }}>
                <AtomicLogo width={64} height={64} />
              </Box>
              <Typography variant="h4" component="h1" gutterBottom>
                Set a new password
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Your account requires a new password. Please create a secure
                password for {email}
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmitNewPassword}>
              <TextField
                fullWidth
                label="New Password"
                type={showNewPassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                margin="normal"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        edge="end"
                      >
                        {showNewPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 1 }}
              />

              <TextField
                fullWidth
                label="Confirm New Password"
                type={showConfirmNewPassword ? "text" : "password"}
                value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)}
                required
                margin="normal"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle confirm password visibility"
                        onClick={() =>
                          setShowConfirmNewPassword(!showConfirmNewPassword)
                        }
                        edge="end"
                      >
                        {showConfirmNewPassword ? (
                          <VisibilityOff />
                        ) : (
                          <Visibility />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 2 }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={
                  loading || !newPassword || newPassword !== confirmNewPassword
                }
                startIcon={
                  loading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <Login />
                  )
                }
                sx={{
                  mb: 2,
                  py: 1.5,
                  backgroundColor: "#3877F7",
                  "&:hover": {
                    backgroundColor: "#2d5fd6",
                  },
                }}
              >
                {loading ? "Submitting..." : "Set Password and Sign In"}
              </Button>

              <Box sx={{ textAlign: "center" }}>
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  onClick={onSwitchToLanding}
                  sx={{ textDecoration: "none" }}
                >
                  ← Use a different email
                </Link>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        background: "#fafafa",
        padding: 2,
      }}
    >
      <Card
        sx={{
          maxWidth: 400,
          width: "100%",
          borderRadius: 2,
          border: "1px solid #e0e0e0",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
        }}
      >
        <CardContent sx={{ padding: 4 }}>
          <Box sx={{ textAlign: "center", mb: 3 }}>
            <Box sx={{ mb: 2 }}>
              <AtomicLogo width={64} height={64} />
            </Box>
            <Typography variant="h4" component="h1" gutterBottom>
              Welcome back
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Enter your password for {email}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 1 }}
            >
              If you were invited, use the temporary password provided to you
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSignIn}>
            <TextField
              fullWidth
              label="Password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              margin="normal"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={togglePasswordVisibility}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2 }}
            />

            <Box sx={{ textAlign: "right", mb: 2 }}>
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={onSwitchToForgotPassword}
                sx={{ textDecoration: "none" }}
              >
                Forgot password?
              </Link>
            </Box>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading || !password}
              startIcon={
                loading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <Login />
                )
              }
              sx={{
                mb: 2,
                py: 1.5,
                backgroundColor: "#3877F7",
                "&:hover": {
                  backgroundColor: "#2d5fd6",
                },
              }}
            >
              {loading ? "Signing In..." : "Sign In"}
            </Button>

            <Box sx={{ textAlign: "center" }}>
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={onSwitchToLanding}
                sx={{ textDecoration: "none" }}
              >
                ← Use a different email
              </Link>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};
